import { isGuid } from "@/pages/DeploymentsPage/application/useAgentPlatform.selector"
import {
  GUARD_LINES,
  GUARD_OPTION
} from "@/pages/DeploymentsPage/components/organisms/AgentPlatformTab"
import {
  DEFAULT_VALUES,
  DEPLOYMENT_KEY_FIELD,
  PACKAGE_SPEC_ARCH
} from "@/pages/DeploymentsPage/const"
import { getShellVariableLine } from "@/pages/DeploymentsPage/helpers/deployment-command"
import { FOUNDRY_DEMO_APP_ID } from "./FoundryTab.constants"

import type {
  AgentPlatform,
  AgentPlatformStep
} from "@/pages/DeploymentsPage/components/organisms/AgentPlatformTab"
import type { DeployCommandLine } from "@/pages/DeploymentsPage/helpers/deployment-command.types"

// Every script line stays short enough to fit the command box. A jq program
// spans several groups of its own: a trailing "\" inside the quotes would
// reach jq.

// The project connection holding the deployment key, and the key's name in it
const CONNECTION_NAME = "kodem"
const CONNECTION_KEY = "deployment-key"

// The Foundry data plane (agents and their versions)
const DATA_PLANE = "az rest --resource https://ai.azure.com"

const VARIABLE_LINES = {
  agentDir: getShellVariableLine("AGENT_DIR", "agentDir"),
  projectId: getShellVariableLine("PROJECT_ID", "projectId"),
  projectEndpoint: getShellVariableLine("PROJECT_ENDPOINT", "projectEndpoint"),
  agentName: getShellVariableLine("AGENT_NAME", "agentName"),
  agentVersion: getShellVariableLine("AGENT_VERSION", "agentVersion"),
  acrName: getShellVariableLine("ACR_NAME", "acrName"),
  image: getShellVariableLine("IMAGE", "image"),
  environment: getShellVariableLine("KODEM_ENVIRONMENT", "environment"),
  // Single quotes: Foundry, not the shell, resolves the placeholder
  keyReference: {
    text: `KEY_REFERENCE='\${{connections.${CONNECTION_NAME}.credentials.${CONNECTION_KEY}}}'`
  }
} satisfies Record<string, DeployCommandLine>

export const getDefaultValues = ({
  environment = DEFAULT_VALUES.ENVIRONMENT
}: { environment?: string } = {}) => ({
  key: Date.now().toString(),
  agentDir: DEFAULT_VALUES.AGENT_DIR,
  projectId: DEFAULT_VALUES.PROJECT_ID,
  projectEndpoint: DEFAULT_VALUES.PROJECT_ENDPOINT,
  agentName: DEFAULT_VALUES.AGENT_NAME,
  agentVersion: DEFAULT_VALUES.AGENT_VERSION,
  acrName: DEFAULT_VALUES.ACR_NAME,
  image: DEFAULT_VALUES.IMAGE,
  deploymentKey: DEFAULT_VALUES.DEPLOYMENT_KEY,
  environment
})

// When Foundry builds the image from the source, the Kodem package at the
// source root starts the sensors from sitecustomize.py once the root is on
// PYTHONPATH (or from "import kodem.autostart" in the entry module)
const getPackageStep = (version: string): AgentPlatformStep => ({
  prefix: "add",
  title: "foundry.packageStep",
  subTitle: "foundry.packageSubtitle",
  lines: {
    zip: {
      text: `ZIP=kortex-${version}-python-${PACKAGE_SPEC_ARCH.AMD64}.zip`
    },
    agentDir: VARIABLE_LINES.agentDir,
    unzip: { text: 'unzip -o "$ZIP" -d "$AGENT_DIR"' }
  },
  groups: [["zip"], ["agentDir"], ["unzip"]]
})

// The deployment key in a CustomKeys connection of the project: Foundry keeps
// its secrets in Key Vault (the project's Key Vault connection, else a managed
// vault) and resolves the connection placeholder of KODEM_DEPLOYMENT_KEY when
// the agent starts, so the key is never in the agent version
const STORE_STEP: AgentPlatformStep = {
  prefix: "store",
  title: "foundry.storeStep",
  subTitle: "foundry.storeSubtitle",
  lines: {
    projectId: VARIABLE_LINES.projectId,
    connectionFile: { text: "cat > kodem-connection.json <<EOF" },
    connection: {
      text: '{"properties": {"category": "CustomKeys", "authType": "CustomKeys",'
    },
    connectionTarget: { text: '"target": "https://kodemsecurity.com",' },
    deploymentKey: {
      text: `"credentials": {"keys": {"${CONNECTION_KEY}": "`,
      name: DEPLOYMENT_KEY_FIELD,
      suffix: '"}}}}'
    },
    connectionEnd: { text: "EOF" },
    put: { text: "az rest --method put -o none" },
    putUrl: { text: `--url "$PROJECT_ID/connections/${CONNECTION_NAME}"` },
    putVersion: { text: "--url-parameters api-version=2025-04-01-preview" },
    putBody: { text: "--body @kodem-connection.json" },
    remove: { text: "rm kodem-connection.json" }
  },
  groups: [
    ["projectId"],
    ["connectionFile"],
    ["connection"],
    ["connectionTarget"],
    ["deploymentKey"],
    ["connectionEnd"],
    ["put", "putUrl", "putVersion", "putBody"],
    ["remove"]
  ]
}

// An agent version is immutable: the deploy creates a new version from the
// given one, with the Kodem environment variables added (and the new image)
const VERSION_LINES = {
  projectEndpoint: VARIABLE_LINES.projectEndpoint,
  agentName: VARIABLE_LINES.agentName,
  agentVersion: VARIABLE_LINES.agentVersion
} satisfies Record<string, DeployCommandLine>

const GET_VERSION_LINES = {
  environment: VARIABLE_LINES.environment,
  keyReference: VARIABLE_LINES.keyReference,
  agentUrl: { text: 'AGENT_URL="$PROJECT_ENDPOINT/agents/$AGENT_NAME"' },
  get: { text: DATA_PLANE },
  getUrl: { text: '--url "$AGENT_URL/versions/$AGENT_VERSION?api-version=v1"' },
  getFile: { text: "> version.json" },
  definition: { text: "JQ_VERSION='{definition: (.definition |" }
} satisfies Record<string, DeployCommandLine>

const CREATE_VERSION_LINES = {
  variables: {
    text: ".environment_variables = ((.environment_variables // {}) + {"
  },
  ...GUARD_LINES,
  jq: {
    text: 'jq --arg env "$KODEM_ENVIRONMENT" --arg key "$KEY_REFERENCE"'
  },
  jqFile: { text: '"$JQ_VERSION" version.json > new-version.json' },
  create: { text: `${DATA_PLANE} --method post` },
  createUrl: { text: '--url "$AGENT_URL/versions?api-version=v1"' },
  createBody: { text: "--body @new-version.json" }
} satisfies Record<string, DeployCommandLine>

// The data plane takes a version with code_configuration only as
// multipart/form-data: the version's metadata and its code zip, with the zip's
// sha256 in a header. az rest sends JSON only, so curl posts it.
const CREATE_SOURCE_VERSION_LINES = {
  zipCode: { text: '(cd "$AGENT_DIR" && zip -qr - .) > agent.zip' },
  zipSha: {
    text: 'ZIP_SHA256="$(shasum -a 256 agent.zip | cut -d " " -f 1)"'
  },
  token: {
    text: 'TOKEN="$(az account get-access-token --resource https://ai.azure.com'
  },
  tokenQuery: { text: '--query accessToken -o tsv)"' },
  create: { text: 'curl -sS -X POST "$AGENT_URL/versions?api-version=v1"' },
  createAuth: { text: '-H "Authorization: Bearer $TOKEN"' },
  createSha: { text: '-H "x-ms-code-zip-sha256: $ZIP_SHA256"' },
  createMetadata: {
    text: '-F "metadata=@new-version.json;type=application/json"'
  },
  createCode: { text: '-F "code=@agent.zip;type=application/zip"' }
} satisfies Record<string, DeployCommandLine>

const VERSION_GROUPS: (keyof typeof VERSION_LINES)[][] = [
  ["projectEndpoint"],
  ["agentName"],
  ["agentVersion"]
]

const GET_VERSION_GROUPS: (keyof typeof GET_VERSION_LINES)[][] = [
  ["environment"],
  ["keyReference"],
  ["agentUrl"],
  ["get", "getUrl", "getFile"],
  ["definition"]
]

const CREATE_GROUP: (keyof typeof CREATE_VERSION_LINES)[] = [
  "create",
  "createUrl",
  "createBody"
]

const DEPLOY_STEPS: Record<"container" | "codeUpload", AgentPlatformStep> = {
  // The image and its digest let the sensors share one SBOM per image
  container: {
    prefix: "deploy",
    title: "foundry.deployStep",
    option: GUARD_OPTION,
    lines: {
      ...VERSION_LINES,
      acrName: VARIABLE_LINES.acrName,
      image: VARIABLE_LINES.image,
      ...GET_VERSION_LINES,
      digestComment: {
        text: "# The image digest: one Kodem scan per image",
        comment: true
      },
      digest: { text: 'DIGEST="$(az acr repository show -n "$ACR_NAME"' },
      digestImage: { text: '--image "${IMAGE#*/}" --query digest -o tsv)"' },
      imageUpdate: { text: ".container_configuration.image = $image |" },
      ...CREATE_VERSION_LINES,
      variablesKodem: {
        text: "KODEM_ENVIRONMENT: $env, KODEM_DEPLOYMENT_KEY: $key,"
      },
      variablesImage: {
        text: "KODEM_AZURE_IMAGE: $image, KODEM_AZURE_IMAGE_DIGEST: $digest}))}'"
      },
      jqArgs: { text: '--arg image "$IMAGE" --arg digest "$DIGEST"' }
    },
    groups: [
      ...VERSION_GROUPS,
      ["acrName"],
      ["image"],
      ["digestComment"],
      ["digest", "digestImage"],
      ...GET_VERSION_GROUPS,
      ["imageUpdate"],
      ["variables"],
      ["variablesGuard"],
      ["variablesGuardMode"],
      ["variablesKodem"],
      ["variablesImage"],
      ["jq", "jqArgs", "jqFile"],
      CREATE_GROUP
    ]
  },
  // The new version keeps the deployed version's definition (Foundry builds
  // its image from the source) and uploads the source with the Kodem package
  codeUpload: {
    prefix: "deploy",
    title: "foundry.deploySourceStep",
    option: GUARD_OPTION,
    lines: {
      ...VERSION_LINES,
      agentDir: VARIABLE_LINES.agentDir,
      ...GET_VERSION_LINES,
      variables: CREATE_VERSION_LINES.variables,
      variablesGuard: CREATE_VERSION_LINES.variablesGuard,
      variablesGuardMode: CREATE_VERSION_LINES.variablesGuardMode,
      variablesKodem: {
        text: "KODEM_ENVIRONMENT: $env, KODEM_DEPLOYMENT_KEY: $key}))}'"
      },
      jq: CREATE_VERSION_LINES.jq,
      jqFile: CREATE_VERSION_LINES.jqFile,
      ...CREATE_SOURCE_VERSION_LINES
    },
    groups: [
      ...VERSION_GROUPS,
      ["agentDir"],
      ...GET_VERSION_GROUPS,
      ["variables"],
      ["variablesGuard"],
      ["variablesGuardMode"],
      ["variablesKodem"],
      ["jq", "jqFile"],
      ["zipCode"],
      ["zipSha"],
      ["token", "tokenQuery"],
      ["create", "createAuth", "createSha", "createMetadata", "createCode"]
    ]
  }
}

// Microsoft Foundry hosted agents run linux/amd64: the kortex-foundry image for
// containers, the amd64 Python package for source deployments
export const getFoundryPlatform = (version: string): AgentPlatform => ({
  i18nKey: "foundry",
  imageName: "kortex-foundry",
  dockerPlatform: "linux/amd64",
  packageSpecArch: PACKAGE_SPEC_ARCH.AMD64,
  steps: {
    container: [STORE_STEP, DEPLOY_STEPS.container],
    codeUpload: [getPackageStep(version), STORE_STEP, DEPLOY_STEPS.codeUpload]
  },
  getDefaultValues
})

// The demo's grant commands, scoped to the subscription once its id is valid
export const getFoundryDemoCommands = (subscriptionId: string) => {
  const subscription = isGuid(subscriptionId)
    ? subscriptionId.trim()
    : "<subscription-id>"

  return {
    servicePrincipal: `az ad sp create --id ${FOUNDRY_DEMO_APP_ID}`,
    roleAssignment: `az role assignment create --assignee ${FOUNDRY_DEMO_APP_ID} --role "Azure AI User" --scope "/subscriptions/${subscription}"`
  }
}
